import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
import { i18n } from '../../../i18n/index.js';
import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { Link, useSearchParams } from 'react-router-dom';
import { useState, type ReactElement } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { Alert, Button, Space, Tag, Tooltip } from 'antd';
import type { ColumnType } from 'antd/es/table/interface';
import { CalendarOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { List } from 'lucide-react';

import { formatDateTime, formatPersonName } from '../../../shared/format/index.js';
import { usePublishSelection } from '../../../shared/selection/useSelectionStore.js';
import { EntityHistoryButton } from '../../activity/components/EntityHistoryButton.js';
import { DeleteEntityButton } from '../../../shared/ui/DeleteEntityButton.js';
import { EntityLink } from '../../../shared/ui/EntityLink.js';
import { EntityTable } from '../../../shared/ui/EntityTable.js';
import { EntityTaskDueChip } from '../../../components/entity-detail/EntityTaskDueChip.js';
import { GridColumnsButton } from '../../../shared/grid/GridColumnsButton.js';
import { useGridProfile, type GridColumnMeta } from '../../../shared/grid/useGridProfile.js';
import { useOrderedColumns } from '../../../shared/grid/useOrderedColumns.js';
import { byDate, byOrder, byText } from '../../../shared/ui/tableSorters.js';
import { meetingIcsUrl, type MeetingItem, type MeetingStatus } from '../api/meetings.client.js';
import { MeetingFormModal } from '../components/MeetingFormModal.js';
import { useDeleteMeetingMutation, useMeetings } from '../hooks/useMeetings.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { PageHeader } from '../../../shared/ui/PageHeader.js';
import { FilterBar, FilterSearch, FilterSelect } from '../../../shared/ui/FilterBar.js';
import {
  ENTITY_NAME_LINK_STYLE,
  LIST_CREATE_BUTTON_STYLE,
  LIST_ICON_BUTTON_STYLE,
  LIST_SURFACE_STYLE,
  SEG_TRACK_STYLE,
  segOptionStyle,
} from '../../../shared/ui/list-styles.js';
import { QuietEmpty, QuietListSkeleton } from '../../../shared/ui/quiet-states.js';
import {
  FLEX_TITLE_CELL_CLASS,
  tableMinWidth as computeTableMinWidth,
} from '../../../shared/ui/table-width.js';
import {
  meetingParticipantLabel,
  meetingParticipantTagStyle,
  STATUS_TONE,
  STATUS_LABEL,
  STATUS_ORDER,
} from '../lib/meetingMeta.js';
import { WeekPlanner } from '../../calendar/components/WeekPlanner.js';
import { meetingToEvent, startOfWeekMonday } from '../../calendar/lib/plannerEvents.js';
import '../components/meeting.css';

const MEETING_GRID_COLUMNS: GridColumnMeta[] = [
  {
    key: 'title',
    get label() {
      return i18n.t('crm.features.map.features.meetings.pages.meetingspage.54');
    },
  },
  {
    key: 'startsAt',
    get label() {
      return i18n.t('crm.features.map.features.meetings.pages.meetingspage.55');
    },
  },
  {
    key: 'endsAt',
    get label() {
      return i18n.t('crm.features.map.features.meetings.pages.meetingspage.56');
    },
  },
  {
    key: 'status',
    get label() {
      return i18n.t('crm.features.map.features.meetings.pages.meetingspage.57');
    },
  },
  {
    key: 'participants',
    get label() {
      return i18n.t('crm.features.map.features.meetings.pages.meetingspage.58');
    },
  },
  {
    key: 'location',
    get label() {
      return i18n.t('crm.features.map.features.meetings.pages.meetingspage.59');
    },
  },
  {
    key: 'link',
    get label() {
      return i18n.t('crm.features.map.features.meetings.pages.meetingspage.60');
    },
  },
  {
    key: 'createdAt',
    get label() {
      return i18n.t('crm.features.map.features.meetings.pages.meetingspage.61');
    },
    hiddenByDefault: true,
  },
];

type MeetingViewMode = 'list' | 'calendar';

const STATUS_FILTER_VALUES: readonly (MeetingStatus | 'all')[] = [
  'all',
  'scheduled',
  'completed',
  'cancelled',
];

export default function MeetingsPage(): ReactElement {
  useI18nSubscription();
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const activeQuery = q.trim();
  const statusParam = searchParams.get('status');
  const statusFilter: MeetingStatus | undefined = (
    STATUS_FILTER_VALUES as readonly string[]
  ).includes(statusParam ?? '')
    ? (statusParam as MeetingStatus)
    : undefined;
  const view: MeetingViewMode = searchParams.get('view') === 'calendar' ? 'calendar' : 'list';

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<MeetingItem | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Видимая неделя календарного вида (Monday-first). В списке фильтра по датам
  // нет: он отдаёт последние записи, а недельному плану нужна открытая неделя.
  const [calendarCursor, setCalendarCursor] = useState<Dayjs>(() => dayjs());

  // Фильтры и вид живут в URL: ссылку на отфильтрованный список можно передать.
  const setParam = (name: 'q' | 'status' | 'view', value: string | undefined): void => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (value === undefined || value === '') next.delete(name);
        else next.set(name, value);
        next.delete('page');
        return next;
      },
      { replace: true },
    );
  };

  const calendarRange =
    view === 'calendar'
      ? {
          from: startOfWeekMonday(calendarCursor).toISOString(),
          to: startOfWeekMonday(calendarCursor).add(6, 'day').endOf('day').toISOString(),
        }
      : null;

  const { data, isLoading, isError, error, refetch } = useMeetings({
    ...(activeQuery ? { q: activeQuery } : {}),
    ...(statusFilter ? { status: statusFilter } : {}),
    ...(calendarRange ? { from: calendarRange.from, to: calendarRange.to } : {}),
  });
  const deleteMut = useDeleteMeetingMutation();

  const meetings = data ?? [];
  // Быстрая заметка цепляется к отмеченным встречам — как в остальных гридах.
  usePublishSelection(
    'meeting',
    meetings
      .filter((meeting) => selectedIds.includes(meeting.id))
      .map((meeting) => ({ id: meeting.id, label: meeting.title })),
  );

  const columnMap: Record<string, ColumnType<MeetingItem>> = {
    title: {
      title: i18n.t('crm.features.features.meetings.pages.meetingspage.тема'),
      key: 'title',
      sorter: byText<MeetingItem>((meeting) => meeting.title),
      // Название ведёт на карточку — как у всех сущностей; правка осталась
      // на кнопке в колонке действий.
      render: (_, meeting) => (
        <Link
          to={`/meetings/${meeting.id}`}
          className={FLEX_TITLE_CELL_CLASS}
          style={ENTITY_NAME_LINK_STYLE}
          title={meeting.title}
        >
          {meeting.title}
        </Link>
      ),
    },
    startsAt: {
      title: i18n.t('crm.features.features.meetings.pages.meetingspage.начало'),
      key: 'startsAt',
      width: 150,
      defaultSortOrder: 'ascend',
      sorter: byDate<MeetingItem>((meeting) => meeting.startsAt),
      // Прошедшую, но не закрытую встречу подсвечиваем чипом — как просроченные
      // задачи и сроки в карточках (общий EntityTaskDueChip).
      render: (_, meeting) => (
        <EntityTaskDueChip dueAt={meeting.startsAt} closed={meeting.status !== 'scheduled'} />
      ),
    },
    endsAt: {
      title: i18n.t('crm.features.features.meetings.pages.meetingspage.окончание'),
      key: 'endsAt',
      width: 145,
      sorter: byDate<MeetingItem>((meeting) => meeting.endsAt),
      render: (_, meeting) => (
        <span className="text-[var(--ant-color-text-secondary)]">
          {formatDateTime(meeting.endsAt)}
        </span>
      ),
    },
    status: {
      title: i18n.t('crm.features.features.meetings.pages.meetingspage.статус'),
      key: 'status',
      width: 120,
      sorter: byOrder<MeetingItem, MeetingStatus>((meeting) => meeting.status, STATUS_ORDER),
      render: (_, meeting) => (
        <EntityStatusChip tone={STATUS_TONE[meeting.status]}>
          {STATUS_LABEL[meeting.status]}
        </EntityStatusChip>
      ),
    },
    participants: {
      title: i18n.t('crm.features.features.meetings.pages.meetingspage.участники'),
      key: 'participants',
      width: 170,
      render: (_, meeting) =>
        meeting.participants.length === 0 ? (
          <span className="text-[var(--ant-color-text-quaternary)]">—</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {meeting.participants.slice(0, 3).map((participant) => (
              <Tag key={participant.id} style={meetingParticipantTagStyle(participant)}>
                {meetingParticipantLabel(participant)}
              </Tag>
            ))}
            {meeting.participants.length > 3 ? (
              <Tag
                style={{
                  fontSize: 11,
                  fontVariantNumeric: 'tabular-nums',
                  background: 'var(--g-fill)',
                  color: 'var(--ant-color-text-tertiary)',
                  borderColor: 'transparent',
                  padding: '0 7px',
                }}
              >
                {i18n.t('crm.features.features.meetings.pages.meetingspage.еще')}
                {meeting.participants.length - 3}
              </Tag>
            ) : null}
          </div>
        ),
    },
    location: {
      title: i18n.t('crm.features.features.meetings.pages.meetingspage.место'),
      key: 'location',
      width: 125,
      sorter: byText<MeetingItem>((meeting) => meeting.location),
      render: (_, meeting) =>
        meeting.location ?? <span className="text-[var(--ant-color-text-quaternary)]">—</span>,
    },
    createdAt: {
      title: i18n.t('crm.features.features.meetings.pages.meetingspage.дата_создания'),
      key: 'createdAt',
      width: 160,
      sorter: byDate<MeetingItem>((meeting) => meeting.createdAt),
      render: (_, meeting) => (
        <span className="text-[var(--ant-color-text-secondary)]">
          {formatDateTime(meeting.createdAt)}
        </span>
      ),
    },
    link: {
      title: i18n.t('crm.features.features.meetings.pages.meetingspage.привязка'),
      key: 'link',
      width: 150,
      render: (_, meeting) => {
        if (meeting.deal) {
          return (
            <EntityLink
              kind="deal"
              id={meeting.deal.id}
              label={meeting.deal.title}
              withKindPrefix
            />
          );
        }
        if (meeting.company) {
          return (
            <EntityLink
              kind="company"
              id={meeting.company.id}
              label={meeting.company.name}
              withKindPrefix
            />
          );
        }
        if (meeting.contact) {
          return (
            <EntityLink
              kind="contact"
              id={meeting.contact.id}
              label={formatPersonName(meeting.contact.firstName, meeting.contact.lastName)}
              withKindPrefix
            />
          );
        }
        return <span className="text-[var(--ant-color-text-quaternary)]">—</span>;
      },
    },
  };

  const actionsColumn: ColumnType<MeetingItem> = {
    title: i18n.t('crm.features.features.meetings.pages.meetingspage.действия'),
    key: 'actions',
    width: 140,
    render: (_, meeting) => (
      <Space size="small">
        <Tooltip title={i18n.t('crm.features.features.meetings.pages.meetingspage.редактировать')}>
          <Button
            size="small"
            type="text"
            aria-label={i18n.t(
              'crm.features.features.meetings.pages.meetingspage.редактировать_встречу',
            )}
            icon={<EditOutlined />}
            onClick={() => {
              setEditing(meeting);
              setFormOpen(true);
            }}
          />
        </Tooltip>
        <Tooltip
          title={i18n.t(
            'crm.features.features.meetings.pages.meetingspage.скачать_ics_файл_для_календаря_участника',
          )}
        >
          <Button
            size="small"
            type="text"
            aria-label={i18n.t(
              'crm.features.features.meetings.pages.meetingspage.скачать_приглашение',
            )}
            icon={<CalendarOutlined />}
            href={meetingIcsUrl(meeting.id)}
            target="_blank"
          />
        </Tooltip>
        {/* Та же кнопка подтверждения, что и у остальных сущностей; отличается
              только глагол: встречу отменяют, а не удаляют. */}
        <EntityHistoryButton
          entity="meeting"
          entityId={meeting.id}
          title={i18n.t('crm.features.features.meetings.pages.meetingspage.история_встречи')}
          subject={meeting.title}
          iconOnly
        />
        <DeleteEntityButton
          iconOnly
          label={i18n.t('crm.features.features.meetings.pages.meetingspage.отменить_встречу')}
          title={i18n.t('crm.features.features.meetings.pages.meetingspage.отменить_встречу_2')}
          description={i18n.t(
            'crm.features.features.meetings.pages.meetingspage.встреча_будет_отменена_и_скрыта_из_списка',
          )}
          okText={i18n.t('crm.features.features.meetings.pages.meetingspage.отменить')}
          loading={deleteMut.isPending && deleteMut.variables === meeting.id}
          onConfirm={() => deleteMut.mutate(meeting.id)}
        />
      </Space>
    ),
  };

  const grid = useGridProfile('meetings', MEETING_GRID_COLUMNS);
  const ordered = useOrderedColumns<MeetingItem>({
    profile: grid,
    columnMap,
    trailing: [actionsColumn],
  });

  // Минимальная ширина таблицы: фиксированные колонки плюс пол под тему. Общий
  // контракт списков (shared/ui/table-width.ts): на 1440 таблица укладывается без
  // внешней прокрутки; на узком экране прокручивается внутри своего контейнера.
  const tableMinWidth = computeTableMinWidth(ordered.columns, { selection: true });

  return (
    <div className="animate-fade-in flex h-full flex-col">
      <PageHeader
        title={i18n.t('crm.features.features.meetings.pages.meetingspage.встречи')}
        subtitle={
          <>
            <span data-testid="meetings-visible-count">{meetings.length}</span>{' '}
            {i18n.tc('crm.counts.meetings', meetings.length)}
          </>
        }
        actions={
          <div className="flex max-w-full flex-wrap items-center gap-2">
            <div
              className="flex items-center overflow-hidden p-0.5"
              role="group"
              aria-label={i18n.t('crm.meetings.list.view_label')}
              style={SEG_TRACK_STYLE}
            >
              <Tooltip title={i18n.t('crm.features.features.meetings.pages.meetingspage.список')}>
                <Button
                  type="text"
                  size="small"
                  aria-label={i18n.t('crm.features.features.meetings.pages.meetingspage.список')}
                  aria-pressed={view === 'list'}
                  onClick={() => setParam('view', undefined)}
                  style={segOptionStyle(view === 'list', true)}
                >
                  <List size={14} />
                </Button>
              </Tooltip>
              <Tooltip
                title={i18n.t('crm.features.features.meetings.pages.meetingspage.календарь')}
              >
                <Button
                  type="text"
                  size="small"
                  aria-label={i18n.t('crm.features.features.meetings.pages.meetingspage.календарь')}
                  aria-pressed={view === 'calendar'}
                  onClick={() => setParam('view', 'calendar')}
                  style={segOptionStyle(view === 'calendar', false)}
                >
                  <CalendarOutlined />
                </Button>
              </Tooltip>
            </div>

            {view === 'list' ? <GridColumnsButton profile={grid} /> : null}

            <Button
              type="primary"
              aria-label={i18n.t('crm.features.features.meetings.pages.meetingspage.новая_встреча')}
              icon={<PlusOutlined />}
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
              style={LIST_CREATE_BUTTON_STYLE}
            >
              {i18n.t('crm.features.features.meetings.pages.meetingspage.новая_встреча')}
            </Button>
            <Tooltip title={i18n.t('crm.meetings.list.refresh')}>
              <Button
                aria-label={i18n.t('crm.meetings.list.refresh')}
                icon={<ReloadOutlined />}
                onClick={() => void refetch()}
                style={LIST_ICON_BUTTON_STYLE}
              />
            </Tooltip>
          </div>
        }
        testId="meetings-page-header"
      />

      <div className="flex min-h-0 flex-1 flex-col gap-3">
        <FilterBar
          search={
            <FilterSearch
              id="meetings-search"
              aria-label={i18n.t('crm.meetings.list.search_label')}
              placeholder={i18n.t(
                'crm.features.features.meetings.pages.meetingspage.поиск_по_теме_описанию_месту',
              )}
              value={q}
              onChange={(value) => setParam('q', value)}
            />
          }
        >
          <FilterSelect
            aria-label={i18n.t('crm.meetings.list.status_label')}
            value={statusFilter ?? 'all'}
            onChange={(value) => setParam('status', value === 'all' ? undefined : value)}
            options={[
              {
                value: 'all',
                label: i18n.t('crm.features.features.meetings.pages.meetingspage.все_статусы'),
              },
              {
                value: 'scheduled',
                label: i18n.t('crm.features.features.meetings.pages.meetingspage.запланирована'),
              },
              {
                value: 'completed',
                label: i18n.t('crm.features.features.meetings.pages.meetingspage.прошла'),
              },
              {
                value: 'cancelled',
                label: i18n.t('crm.features.features.meetings.pages.meetingspage.отменена'),
              },
            ]}
          />
        </FilterBar>

        <div className="flex min-h-0 flex-1 flex-col gap-3">
          {deleteMut.isError ? (
            <Alert
              type="error"
              showIcon
              closable
              title={i18n.t(
                'crm.features.features.meetings.pages.meetingspage.не_удалось_отменить_встречу',
              )}
              description={(deleteMut.error as Error).message}
            />
          ) : null}

          {isError ? (
            <ErrorState
              title={i18n.t(
                'crm.features.features.meetings.pages.meetingspage.не_удалось_загрузить_встречи',
              )}
              error={error}
              onRetry={() => void refetch()}
            />
          ) : view === 'calendar' ? (
            // Недельный план показывает пустую неделю сам — Empty здесь был бы
            // вместо сетки. Только встречи: у таба один источник данных.
            <WeekPlanner
              events={meetings.map((meeting) => meetingToEvent(meeting))}
              cursor={calendarCursor}
              onCursorChange={setCalendarCursor}
              loading={isLoading}
              availableTypes={['meeting']}
            />
          ) : (
            <div
              className="min-h-0 flex-1 crm-checkbox-square"
              data-testid="meetings-list-view"
              style={LIST_SURFACE_STYLE}
            >
              {isLoading ? (
                <QuietListSkeleton rows={6} />
              ) : (
                <EntityTable<MeetingItem>
                  className="meetings-grid"
                  tableLayout="fixed"
                  scroll={{ x: tableMinWidth }}
                  dataSource={meetings}
                  columns={ordered.columns}
                  components={ordered.components}
                  rowSelection={{
                    columnWidth: 48,
                    selectedRowKeys: selectedIds,
                    onChange: (keys) => setSelectedIds(keys.map(String)),
                  }}
                  locale={{
                    emptyText: (
                      <QuietEmpty
                        text={i18n.t(
                          'crm.features.features.meetings.pages.meetingspage.встреч_пока_нет_назначьте_первую_кнопкой_н',
                        )}
                      />
                    ),
                  }}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {formOpen ? (
        <MeetingFormModal open onClose={() => setFormOpen(false)} meeting={editing} />
      ) : null}
    </div>
  );
}
